import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { randomUuid } from '@/lib/core/utils';
import {
  ParkingBookFlowConfirmationElementContentModelContentType,
  ParkingBookFlowConfirmationPropertiesModel,
  ParkingBookFlowMoreDetailsElementContentModelContentType,
  ParkingBookFlowMoreDetailsPropertiesModel,
  ParkingFlowWhereHowManyWhenElementContentModelContentType,
  ParkingFlowWhereHowManyWhenPropertiesModel,
  SeatingReservationDto,
} from '@/services/platform/api/model';
import type { BookingState, BookFlowParkingProps } from './types';
import { ParkingBook } from './book';
import { ParkingDetails } from './details';
import { ConfirmationBook } from './confirmation/book';
import { ConfirmationRequest } from './confirmation/request';

export type { BookingState };

// ── BookFlowParking ───────────────────────────────────────────────────────────

const makeEmptyBooking = (): BookingState => ({
  service: null,
  venue: null,
  guestCount: null,
  date: null,
  room: null,
  time: null,
  isRequestAnyway: false,
  notes: '',
  groupNotes: {},
  selectedTags: [],
  selectedGroupTags: [],
  idempotencyKey: randomUuid(),
  seatingAreaPublicGuid: undefined,
});

export function BookFlowParking({ content, context }: BookFlowParkingProps) {
  const navigate = useNavigate();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [reservation, setReservation] = useState<SeatingReservationDto | null>(null);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);
  const [booking, setBooking] = useState<BookingState>(makeEmptyBooking);

  const steps = content.properties?.steps?.items ?? [];

  const handleBack = () => {
    if (step === 0) navigate(-1);
    else setStep(prev => (prev - 1) as 0 | 1 | 2);
  };

  const handleNext = () => setStep(prev => (prev + 1) as 0 | 1 | 2);

  const handleRequestSuccess = (res: SeatingReservationDto) => {
    setReservation(res);
    setStep(2);
  };

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      {steps.map((stepItem, index) => {
        if (index !== step) return null;
        const props = stepItem.content?.properties;
        const stepType = stepItem.content?.contentType;
        switch (stepType) {
          case ParkingFlowWhereHowManyWhenElementContentModelContentType.parkingFlowWhereHowManyWhen:
            return (
              <ParkingBook
                key={index}
                cmsProps={props as ParkingFlowWhereHowManyWhenPropertiesModel}
                booking={booking}
                onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
                onBack={handleBack}
                onContinue={handleNext}
                onRequestSuccess={handleRequestSuccess}
                context={context}
                step={index + 2}
              />
            );
          case ParkingBookFlowMoreDetailsElementContentModelContentType.parkingBookFlowMoreDetails:
            return (
              <ParkingDetails
                key={index}
                cmsProps={props as ParkingBookFlowMoreDetailsPropertiesModel}
                booking={booking}
                onBookingChange={(updates: Partial<BookingState>) =>
                  setBooking(prev => ({ ...prev, ...updates }))
                }
                onBack={handleBack}
                onSuccess={res => {
                  setReservation(res);
                  setStep(2);
                }}
                step={index + 2}
              />
            );
          case ParkingBookFlowConfirmationElementContentModelContentType.parkingBookFlowConfirmation:
            if (!reservation) return null;
            return booking.isRequestAnyway ? (
              <ConfirmationRequest
                key={index}
                booking={booking}
                reservation={reservation}
                onGoHome={() => navigate('/')}
                onCancelRequest={() => navigate(-1)}
              />
            ) : (
              <ConfirmationBook
                key={index}
                booking={booking}
                reservation={reservation}
                cmsProps={props as ParkingBookFlowConfirmationPropertiesModel}
                onGoHome={() => navigate('/')}
                onSeeBookingDetails={() =>
                  navigate(
                    `${context?.settings?.properties?.bookingDetail?.route?.path}/${reservation.publicGuid}`
                  )
                }
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
